import { NovatiqAuthorizationClient } from '@/_auth-client';
import {
  AuthorizationHelper,
  NovatiqLoadingSpinnerHelper,
  NovatiqPopupHelper,
  PasswordExpiryResetHelper
} from '@/_helpers';
import { LanguageService } from '@/_services/language.service';
import { PageSettingService } from '@/features/administration/data/page-setting.service';
import { AuthorizationService } from '@/features/authorization/data/authorization.service';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { ROUTES } from './_constants';
import { AppStorage } from './_storage';
import { IUserInfo } from './_types';
import { LayoutService } from './layouts/service/app.layout.service';
import { DEFAULT_TOAST_LIFE_MS, NovatiqMessageService } from './layouts/service/message.service';
import { NovatiqExpiryLockComponent, NovatiqLoadingSpinnerComponent, NovatiqPopupComponent } from './shared/components';

@Component({
  selector: 'app-body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    TranslateModule,
    Toast,
    Button,
    NovatiqPopupComponent,
    NovatiqExpiryLockComponent,
    NovatiqLoadingSpinnerComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  currentUser: IUserInfo;
  pageSettings: any;
  private isAppIdle: boolean;
  private isIdleWatchOn: boolean;
  private timeOut: any;
  public isShowPopup = false;

  private getCurrentUserSubscription: Subscription;
  private getCurrentLoggedInUserSubscription: Subscription;
  private onIdleStartSubcription: Subscription;
  private onIdleEndSubcription: Subscription;
  private routerEventSubscription: Subscription;
  private passwordResetWarningSubscription: Subscription;

  private readonly authorizationClient = inject(NovatiqAuthorizationClient);
  private readonly authorizationHelper = inject(AuthorizationHelper);
  private readonly passwordExpiryResetHelper = inject(PasswordExpiryResetHelper);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly idle = inject(Idle);
  private readonly loadingSpinner = inject(NovatiqLoadingSpinnerHelper);
  private readonly pageSettingsService = inject(PageSettingService);
  private readonly popupHelper = inject(NovatiqPopupHelper);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly layoutService = inject(LayoutService);
  private readonly languageService = inject(LanguageService);
  private readonly appStorage = inject(AppStorage);
  private readonly messageService = inject(MessageService);
  private readonly novatiqMessageService = inject(NovatiqMessageService);

  ngOnInit() {
    this.setupPrimeNG();
    this.initialiseProperties();
    this.configureIdleTimer();
    this.getCurrentLoggedInUser();
    this.initialiseRouterEvent();
    this.getPageSettings();
    this.languageService.initLanguage();
  }

  setupPrimeNG() {
    // v18_upgrade removed as of v18
    //this.primengConfig.ripple = true; //enables core ripple functionality

    //optional configuration with the default configuration
    this.layoutService.setConfig({
      ripple: true,
      inputStyle: 'outlined',
      menuMode: 'static',
      colorScheme: 'light',
      theme: 'saga-purple',
      scale: 14
    });
  }

  getCurrentLoggedInUser() {
    this.getCurrentUserSubscription = this.authorizationClient.getToken().subscribe((tokenInfo) => {
      clearTimeout(this.timeOut);
      if (tokenInfo) {
        this.timeOut = setTimeout(() => {
          this.authorizationHelper.refreshToken();
        }, tokenInfo.refreshTokenIn);
        if (!this.isIdleWatchOn) {
          this.resetIdle(tokenInfo);
        }
      } else {
        this.idle.stop();
        this.isIdleWatchOn = false;
      }
    });

    this.getCurrentLoggedInUserSubscription = this.authorizationClient.getCurrentUser().subscribe((data) => {
      // get logged-in user data
      this.currentUser = data;
    });
  }

  resetIdle(tokenInfo = null) {
    this.isIdleWatchOn = true;
    this.idle.setIdle(tokenInfo?.expiresIn || this.authorizationClient.expiresIn);
    this.idle.watch();
  }

  resumeUserSession(event: any) {
    this.isAppIdle = false;
    this.resetIdle(event);
  }

  @HostListener('window:beforeunload')
  beforeunloadApplication() {
    if (this.isAppIdle) {
      this.isShowPopup = false;
      this.popupHelper.close();
      this.authorizationClient.removeUserSession();
    }
  }

  private configureIdleTimer() {
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.onIdleStartSubcription = this.idle.onIdleStart.subscribe(() => {
      this.isAppIdle = true;
      this.isShowPopup = true;
      this.popupHelper.open(this.pageSettings?.expiryLockPopupId);
    });

    this.onIdleEndSubcription = this.idle.onIdleEnd.subscribe(() => {
      this.resetIdle();
    });
  }

  private getPageSettings() {
    this.pageSettingsService.get(ROUTES.APP.title).subscribe(
      (settings) => {
        this.pageSettings = settings;
      },
      (error) => {
        throw error;
      }
    );
  }

  private initialiseProperties() {
    this.isIdleWatchOn = false;
    this.isAppIdle = false;
  }

  private initialiseRouterEvent() {
    this.routerEventSubscription = this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      this.checkAndShowPasswordExpiryMessage(evt.url);
      window.scrollTo(0, 0);
      switch (true) {
        case evt instanceof NavigationStart: {
          this.loadingSpinner.start();
          break;
        }
        case evt instanceof NavigationEnd:
        case evt instanceof NavigationCancel:
        case evt instanceof NavigationError: {
          this.loadingSpinner.stop();
          break;
        }
      }
    });
  }

  private checkAndShowPasswordExpiryMessage(url: string) {
    // Check if user is on dashboard and has pending password expiry message
    const isDashboard = url.includes('/dashboard') || url.includes('/campaigndashboard');
    const pendingMessage: string = this.passwordExpiryResetHelper.getPendingPasswordExpiryMessage();
    let translationKey = 'passwordExpiry.passwordExpiryInXDays';
    let translatedMessage = '';
    if (isDashboard && pendingMessage) {
      // Show the toast now that user is on dashboard
      if (pendingMessage.toLowerCase().includes('today')) {
        translationKey = 'passwordExpiry.passwordExpiryToday';
        // Show the toast with translated message
        translatedMessage = this.translate.instant(translationKey);
      } else {
        const daysMatch = pendingMessage.match(/(\d+)\s+day/);
        const daysRemaining = daysMatch ? parseInt(daysMatch[1]) : 0;
        translatedMessage = this.translate.instant(translationKey, { days: daysRemaining });
      }

      this.novatiqMessageService.sendMessage(translatedMessage, '', 15000, 'passwordReset');
      // Clear the pending expiry message
      this.passwordExpiryResetHelper.removePendingPasswordExpiryMessage();
    }
  }

  onPasswordReset() {
    this.passwordResetWarningSubscription = this.authorizationService
      .initiatePasswordResetWarning(this.currentUser.userName)
      .subscribe(
        (responseData: any) => {
          this.passwordExpiryResetHelper.saveResetTokenData({
            expiredUsername: this.currentUser.userName,
            resetToken: responseData.resetToken,
            authToken: responseData.authToken,
            require2FA: responseData.require2FA ? 'true' : 'false'
          });
          // navigate to expired-password page
          this.router.navigateByUrl(`/${ROUTES.PASSWORD_EXPIRED.path}?mode=warning`);
        },
        (error: any) => {
          this.messageService.clear();
          this.messageService.add({
            severity: 'error',
            detail: 'Error Occurred while resetting password',
            life: DEFAULT_TOAST_LIFE_MS
          });
        }
      );
  }

  ngOnDestroy() {
    this.getCurrentUserSubscription?.unsubscribe();
    this.onIdleStartSubcription?.unsubscribe();
    this.onIdleEndSubcription?.unsubscribe();
    this.routerEventSubscription?.unsubscribe();
    this.getCurrentLoggedInUserSubscription?.unsubscribe();
    this.passwordResetWarningSubscription?.unsubscribe();
    clearTimeout(this.timeOut);
    this.beforeunloadApplication();
  }
}
